import { TFunction } from 'react-i18next'
import { SpecialDemand } from '../../../calculators/special-demand-ids'
import {
  BuildingFragment,
  LocalGridFragment,
  ProjectVersionFragment,
} from '../../../generated/graphql'

export type SystemContainer = BuildingFragment | LocalGridFragment

export type Dictionaries = {
  providerIdsToNames: Record<string, string>
  demandIdsToNames: Record<string, string>
  containerIdsToNames: Record<string, string>
  versionIdsToBuildingNames: Record<string, string[]>
  providerIdsToContainerNames: Record<string, string>
  versionIdsToNames: Record<string, string>
  costIdsToVersionIds: Record<string, string>
  getSystemContainerTitle: (container: SystemContainer) => string
}

export const emptyDictionaries: Dictionaries = {
  providerIdsToNames: {},
  demandIdsToNames: {},
  containerIdsToNames: {},
  versionIdsToBuildingNames: {},
  providerIdsToContainerNames: {},
  versionIdsToNames: {},
  costIdsToVersionIds: {},
  getSystemContainerTitle: () => '',
}

const isSystemContainerBuilding = (
  container: SystemContainer,
): container is BuildingFragment =>
  (container as BuildingFragment).name !== undefined

export function createDictionaries(
  projectVersions: ProjectVersionFragment[],
  t: TFunction,
): Dictionaries {
  const buildings =
    projectVersions.flatMap((version) => version.buildings) ?? []
  const localGrids =
    projectVersions.flatMap((version) => version.localGrids) ?? []

  const defaultNames: Record<string, string> = {
    [SpecialDemand.Buildings]: t('localGrid.tabs.energySystem.buildings'),
    [SpecialDemand.LocalWaterGrid]: t('localGrid.tabs.energySystem.waterGrid'),
    [SpecialDemand.OtherProvidersElectricity]: t(
      `localGrid.tabs.energySystem.otherProviders`,
    ),
  }

  const getSystemContainerTitle = (container: SystemContainer): string =>
    isSystemContainerBuilding(container)
      ? container.name
      : t(`localGridType.${container.type}`)

  const providerIdsToNames =
    [...buildings, ...localGrids]
      .flatMap((systemContainer) => systemContainer.providers)
      .reduce(
        (records, provider) => ({
          ...records,
          [provider.id]: provider.name,
        }),
        defaultNames,
      ) ?? defaultNames

  const demandIdsToNames =
    buildings
      .flatMap((building) => building.demands)
      .reduce(
        (records, demand) => ({
          ...records,
          [demand.id]: demand.hourSeries.name,
        }),
        defaultNames,
      ) ?? defaultNames

  const containerIdsToNames = [...buildings, ...localGrids].reduce(
    (records, container) => ({
      ...records,
      [container.id]: getSystemContainerTitle(container),
    }),
    {},
  )

  const versionIdsToBuildingNames = projectVersions.reduce(
    (record, version) => ({
      ...record,
      [version.id]: version.buildings.map((building) => building.name),
    }),
    {},
  )

  const providerIdsToContainerNames = [...buildings, ...localGrids].reduce(
    (records, container) => ({
      ...records,
      ...container.providers.reduce(
        (nestedRecods, provider) => ({
          ...nestedRecods,
          [provider.id]: getSystemContainerTitle(container),
        }),
        {},
      ),
    }),
    {},
  )

  const versionIdsToNames = projectVersions.reduce(
    (records, version) => ({ ...records, [version.id]: version.name }),
    {},
  )

  const costIdsToVersionIds = projectVersions.reduce(
    (records, version) => ({
      ...records,
      ...version.costs.reduce(
        (nestedRecords, cost) => ({
          ...nestedRecords,
          [cost.id]: version.id,
        }),
        {},
      ),
    }),
    {},
  )

  return {
    providerIdsToNames,
    demandIdsToNames,
    containerIdsToNames,
    versionIdsToBuildingNames,
    providerIdsToContainerNames,
    versionIdsToNames,
    costIdsToVersionIds,
    getSystemContainerTitle,
  }
}
